import React, { useState, useEffect } from "react";
import { makeStyles, Typography } from "@material-ui/core";
import Icon from "@jll/icon";
import LightTooltip from "../../LightTooltip";
import {
  getAttributeValue,
  getAttributeUnitValue,
} from "../../../Utilities/Functions";
import SearchableSelect from "../SearchableSelect";
import TextField from "../TextField";
import DateField from "../DateField";
import CurrencyInput from "../CurrencyInput";
import TextArea from "../TextArea";
import MaskedField from "../MaskedField";
import NumberField from "../NumberField";

import styles from "../styles";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

const AttributeInput = (props) => {
  const { attr, maskSensitive, onChange, onUnitChange, listOptions } = props;
  const {
    id,
    name,
    source_value,
    user_value,
    source_unit,
    user_unit,
    editable,
    sensitive,
    searchable,
    data_type: dataType,
    mandatory_kyc,
    mandatory_aml,
    validation_pattern: validationPattern,
    min_value: minValue,
    max_value: maxValue,
    min_length: minLength,
    max_length: maxLength,
  } = attr;
  const { t } = useTranslation();

  const label = t(`labels.record.attributes.${id}`) || name;
  const [value, setValue] = useState(null);
  const [unitValue, setUnitValue] = useState("");
  const [labelIcon, setLabelIcon] = useState();
  const [regEx, setRegEx] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [displayType, setDisplayType] = useState();
  const [required, setRequired] = useState();

  const classes = useStyles();

  useEffect(() => {
    let displayType;
    if (maskSensitive && sensitive) {
      displayType = "MT";
    } else if (
      dataType === "L" ||
      dataType === "CL" ||
      dataType === "RL" ||
      dataType === "EL"
    ) {
      if (searchable) {
        displayType = "SL";
      } else {
        displayType = "SL";
      }
    } else if (dataType === "N") {
      displayType = "N";
    } else if (dataType === "D") {
      displayType = "D";
    } else if (dataType === "LS") {
      displayType = "LS";
    } else if (dataType === "CR") {
      displayType = "CR";
    } else {
      displayType = "T";
    }

    if (displayType !== "L" && displayType !== "SL") {
      setValue(getAttributeValue(attr));
      if (displayType === "CR") {
        setUnitValue(getAttributeUnitValue(attr) || "");
      }
    }

    setDisplayType(displayType);
  }, [attr, dataType, maskSensitive, sensitive, searchable]);

  useEffect(() => {
    if (listOptions && listOptions.length > 0) {
      const val = getAttributeValue(attr);
      //if (searchable) {
      const option = listOptions.find((o) => o.value === val);
      if (dataType !== "CR") {
        setValue(option || null);
      }
      //} else {
      //  setValue(val || "");
      // }
    }
  }, [attr, displayType, listOptions]);

  useEffect(() => {
    if (validationPattern) {
      try {
        setRegEx(RegExp(validationPattern));
      } catch (e) {
        console.log("error validating regular expression " + validationPattern);
      }
    }
  }, [validationPattern]);

  useEffect(() => {
    setRequired(mandatory_kyc || mandatory_aml);
  }, [mandatory_kyc, mandatory_aml]);

  useEffect(() => {
    if (
      (source_value === null && source_unit === null) ||
      (user_value === null && user_unit === null) ||
      (source_value === user_value && source_unit === user_unit)
    ) {
      setLabelIcon(null);
    } else {
      const unit = source_unit ? `(${source_unit})` : "";
      //check if list type and replace id with value
      let formatedSourceVal = "";
      if (
        dataType === "L" ||
        dataType === "CL" ||
        dataType === "RL" ||
        dataType === "EL"
      ) {
        const val = listOptions.find((o) => o.value === source_value);
        formatedSourceVal = val ? val.label : "";
      } else {
        formatedSourceVal = source_value;
      }
      setLabelIcon(
        <LightTooltip
          title={<span>{`${formatedSourceVal}${unit}`}</span>}
          placement="top-start"
          style={{ paddingBottom: 0, paddingTop: 0 }}
        >
          <Typography component="span" className={classes.iconToolTip}>
            <Icon className="fas fa-user-edit" style={{ marginRight: 5 }} />
          </Typography>
        </LightTooltip>
      );
    }
  }, [user_value, user_unit, source_value, source_unit, listOptions]);

  const validate = (value) => {
    if (regEx && !regEx.test(value)) {
      setErrorMessage(t("messages.record.attributes.invalidInput"));
      return false;
    }
    if (
      minValue !== null &&
      minValue !== undefined &&
      Number(value) < minValue
    ) {
      setErrorMessage(
        t("messages.record.attributes.minValueError", { minValue })
      );
      return false;
    }
    if (
      maxValue !== null &&
      maxValue !== undefined &&
      Number(value) > maxValue
    ) {
      setErrorMessage(
        t("messages.record.attributes.maxValueError", { maxValue })
      );
      return false;
    }
    const len = value === null || value === undefined ? 0 : value.length;
    if (minLength !== null && minLength !== undefined && len < minLength) {
      setErrorMessage(
        t("messages.record.attributes.minTextLengthError", { minLength })
      );
      return false;
    }
    if (maxLength !== null && maxLength !== undefined && len > maxLength) {
      setErrorMessage(
        t("messages.record.attributes.maxTextLengthError", {
          maxLength: maxLength,
        })
      );
      return false;
    }
    return true;
  };

  const handleChange = (value) => {
    try {
      if (validate(value)) {
        setErrorMessage("");
        setValue(value);
        if (onChange !== undefined && typeof onChange === "function") {
          onChange(id, value);
        }
      }
    } catch (e) {
      console.log("error validating regular expression " + validationPattern);
    }
  };

  const handleUnitDropDownChange = (value) => {
    setUnitValue(value);
    if (onUnitChange !== undefined && typeof onUnitChange === "function") {
      onUnitChange(id, value);
    }
  };

  const handleSearchableDownChange = (value) => {
    setValue(value);
    if (onChange !== undefined && typeof onChange === "function") {
      onChange(id, value ? value.value : "");
    }
  };

  const renderAttribute = () => {
    switch (displayType) {
      case "MT":
        return (
          <MaskedField
            label={label}
            labelIcon={labelIcon}
            required={required}
          />
        );
      case "T":
        return (
          <TextField
            initialValue={value === null || value === undefined ? "" : value}
            label={label}
            labelIcon={labelIcon}
            disabled={!editable}
            required={required}
            onChange={handleChange}
            minLength={maxLength}
            maxLength={maxLength}
          />
        );
      case "N":
        return (
          <NumberField
            initialValue={value === null || value === undefined ? "" : value}
            label={label}
            labelIcon={labelIcon}
            type={"number"}
            disabled={!editable}
            required={required}
            onValueChange={handleChange}
            minValue={minValue}
            maxValue={maxValue}
          />
        );
      case "D":
        return (
          <DateField
            initialValue={value}
            label={label}
            labelIcon={labelIcon}
            disabled={!editable}
            required={required}
            onChange={handleChange}
          />
        );
      case "L":
        return (
          <SearchableSelect
            label={label}
            labelIcon={labelIcon}
            options={listOptions}
            allowFreeTest={false}
            value={value}
            disabled={!editable}
            required={required}
            changeHandler={handleSearchableDownChange}
          />
        );
      case "CR":
        return (
          <CurrencyInput
            initialValue={value === null || value === undefined ? "" : value}
            initialUnitValue={unitValue}
            label={label}
            labelIcon={labelIcon}
            disabled={!editable}
            required={required}
            currencies={listOptions}
            onValueChange={handleChange}
            onUnitCHange={handleUnitDropDownChange}
          />
        );
      case "SL":
        return (
          <SearchableSelect
            label={label}
            labelIcon={labelIcon}
            options={listOptions}
            allowFreeTest={false}
            value={value}
            changeHandler={handleSearchableDownChange}
            disabled={!editable}
            required={required}
          />
        );
      case "LS":
        return (
          <TextArea
            label={label}
            labelIcon={labelIcon}
            rowsMax={3}
            placeholder={t("labels.record.attributes.enterText")}
            initialValue={value === null || value === undefined ? "" : value}
            required={required}
            onChange={handleChange}
            minLength={maxLength}
            maxLength={maxLength}
            disabled={!editable}
            maxHeight={50}
          />
        );
      default:
    }
  };

  return (
    <>
      {renderAttribute()}
      {errorMessage && <div className={classes.errorText}>{errorMessage} </div>}
    </>
  );
};

export default AttributeInput;
